import {
  AuditNode,
  ComputedNodeStatus,
  Node,
  NodeResourceOffering,
  NodeResourceOfferingWithSoleTenancy,
  NodeResponse,
  NodeStatePhase,
  NodeTaint,
} from '@cast/types';

import { isSoleTenantNode } from 'features/cluster/nodes/utils/isSoleTenantNode';
import { isNodeFailedToDrain } from '../cluster';

export const isFallbackNode = (node: NodeResponse | AuditNode): boolean => {
  return (
    ('isSpotFallback' in node && node.isSpotFallback) ||
    Boolean(node.labels?.['scheduling.cast.ai/spot-fallback'])
  );
};

const getPricingType = (
  node: NodeResponse
): NodeResourceOfferingWithSoleTenancy => {
  if (isSoleTenantNode(node)) {
    return 'sole-tenant';
  }
  if (isFallbackNode(node)) {
    return NodeResourceOffering.FALLBACK;
  }
  if (node.spotConfig?.isSpot) {
    return NodeResourceOffering.SPOT;
  }
  return NodeResourceOffering.ON_DEMAND;
};

export const hasDisruptedTaint = (node: NodeResponse): boolean => {
  return (node.taints ?? []).some(
    (taint: NodeTaint) =>
      taint.key === 'karpenter.sh/disrupted' && taint.effect === 'NoSchedule'
  );
};

export const enhanceNode = (node: NodeResponse): Node => {
  let phase: NodeStatePhase | undefined;

  if (
    hasDisruptedTaint(node) &&
    node.state?.phase &&
    node.state.phase === 'ready'
  ) {
    phase = ComputedNodeStatus.DISRUPTED;
  } else if (
    node.unschedulable &&
    node.state?.phase &&
    node.state.phase === 'ready'
  ) {
    phase = ComputedNodeStatus.CORDONED;
  } else {
    phase = node.state?.phase as NodeStatePhase | undefined;
  }

  return {
    ...node,
    state: {
      phase,
    },
    resourceOffering: getPricingType(node),
    unremovable:
      node.labels?.['autoscaling.cast.ai/removal-disabled'] === 'true',
    castNodeId: node.labels?.['provisioner.cast.ai/node-id'],
    isFailedToDrain: isNodeFailedToDrain(node),
  };
};
